<template>
  <v-dialog
    v-model="isOpen"
    width="700"
    scrollable
    :persistent="loading"
    @click:outside="close"
  >
    <v-card>
      <v-card-title class="text-h6">
        {{ member.id ? "Editar técnico" : "Adicionar técnico" }}
      </v-card-title>

      <v-card-text class="pa-4 pt-0">
        <v-select
          v-model="platformsId"
          :items="platforms"
          item-text="name"
          item-value="id"
          label="Plataforma"
          outlined
          class="mt-1 mb-3"
          dense
          multiple
          :disabled="!!member.id"
          hide-details
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar size="32">
              <v-img :src="item.logos.favicon" />
            </v-list-item-avatar>
            <v-list-item-title> {{ item.name }}</v-list-item-title>
          </template>
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center gap-2 mr-4">
              <v-img
                :src="item.logos.favicon"
                width="18"
                height="18"
                class="rounded-xl"
              />
              <v-list-item-title> {{ item.name }}</v-list-item-title>
            </div>
          </template>
        </v-select>

        <phone-input
          v-if="!member.id"
          :phone="member.phone"
          @update:phone="(e) => (member.phone = e)"
          :ddi="member.ddi"
          @update:ddi="(e) => (member.ddi = e)"
          @valid="(v) => (valid = v)"
          dense
          outlined
          :disabled="loading || !!member.id"
        />
        <v-card
          outlined
          v-else
          class="d-flex gap-2 align-center mb-3 pa-4 rounded-lg"
        >
          <base-avatar
            v-if="!!member.name"
            :src="member.photo"
            :seed="member.id"
            :size="50"
          />
          <v-avatar v-else size="50">
            <v-icon large>mdi-account-question</v-icon>
          </v-avatar>
          <div>
            <h6 class="mb-0">{{ member.name || "Conta não criada" }}</h6>
            <span>{{ member.phone }}</span>
          </div>
        </v-card>
        <v-form @submit.prevent v-model="valid">
          <div class="d-flex align-center justify-space-between">
            <p class="text--primary font-weight-bold mb-1 ma-0">Permissões</p>
            <v-btn x-small depressed class="primary" @click="toggleAll(null)">
              {{
                permissionsArray.length === member.permissions.length
                  ? "Desmarcar todos"
                  : "Marcar todos"
              }}
            </v-btn>
          </div>

          <div class="d-flex flex-column gap-2">
            <v-card
              :key="name"
              v-for="[name, perms] in Object.entries(permissionsGroups)"
              class="rounded-lg pa-3"
              elevation="0"
              outlined
            >
              <div class="d-flex align-center justify-space-between">
                <p class="text-subtitle-2 font-weight-black mb-0">{{ name }}</p>
                <v-btn small text @click.stop="toggleAll(perms)">
                  {{
                    perms.some(
                      (p) => !(member?.permissions || []).includes(`${p}`)
                    )
                      ? "Marcar todos"
                      : "Desmarcar todos"
                  }}
                </v-btn>
              </div>
              <v-row dense class="mx-0">
                <v-col
                  v-for="p in perms"
                  :key="p"
                  cols="12"
                  sm="6"
                  md="4"
                  class="pt-0"
                >
                  <v-checkbox
                    class="mt-0"
                    hide-details
                    v-model="member.permissions"
                    :label="permissions[p]"
                    :value="`${p}`"
                    :rules="[
                      (v) => !!v || 'Permissão é obrigatória',
                      (v) => !!v.length || 'Permissão é obrigatória',
                    ]"
                  />
                </v-col>
              </v-row>
            </v-card>
          </div>
        </v-form>
        <v-alert v-if="!!error" outlined text color="error">
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="!!member.id"
          color="danger"
          text
          :disabled="loading"
          :loading="loading"
          @click="remove"
        >
          Remover
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text :disabled="loading" @click="close"> Cancelar </v-btn>
        <v-btn
          color="primary"
          :disabled="
            loading ||
            !(member.permissions || []).length ||
            (!member.id && !valid)
          "
          :loading="loading"
          @click="save"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TECHNICIANS from "@/services/admin/technicians";
import PhoneInput from "../../global/PhoneInput.vue";
import { reducePermissions } from "@/utils/permissionBits";

// Chaves = hex de 2^index (ver ticketme-backend/src/config/permissions.js)
const defaultMember = () => ({
  id: null,
  phone: "",
  ddi: 55,
  permissions: [],
});
export default {
  components: { PhoneInput },
  props: {
    platforms: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    isOpen: false,
    loading: false,
    valid: false,
    error: false,
    member: defaultMember(),
    platform: null,
    platformsId: [],
    permissions: {
      100: "Gerenciar Organização",
      8: "Gerenciar Membros",
      2: "Gerenciar Eventos",
      4: "Encerrar Eventos",
      10: "Gerenciar Setores e Lotes",
      20: "Vender Ingressos Pix",
      400: "Vender Ingressos Presencialmente",
      2000: "Gerenciar Vendas Externas",
      40: "Gerenciar Compras",
      80: "Estornar Pagamentos",
      10000: "Gerenciar Promoções e Cupons",
      80000: "Gerenciar Cortesias",
      20000: "Gerenciar Entrada",
      40000: "Registrar Entrada",
      200: "Ver Relatórios",
      800: "Ver Financeiro",
      1000: "Solicitar Repasses",
      400000: "Gerenciar Planos de Filiação",
      800000: "Gerenciar Membros Filiados",
      1000000: "Gerenciar Produtos",
      2000000: "Gerenciar Cardápios",
      4000000: "Gerenciar Regras de Venda",
      8000000: "Ver Relatórios",
      10000000: "Gerenciar Equipamentos",
      20000000: "Ver relatórios",
      40000000: "Realizar Sangria",
      80000000: "Gerenciar Estacionamento",
      100000000: "Gerenciar PDV de Estacionamento",
      200000000: "Ver Relatórios de Estacionamento",
    },
    permissionsGroups: {
      Organização: ["100", "8"],
      Eventos: [
        "2",
        "4",
        "10",
        "20",
        "400",
        "2000",
        "40",
        "80",
        "10000",
        "80000",
        "20000",
        "40000",
        "200",
      ],
      POS: ["10000000", "20000000", "40000000"],
      "Comidas e Bebidas": ["1000000", "2000000", "4000000", "8000000"],
      Estacionamento: ["80000000", "100000000", "200000000"],
      Filiação: ["400000", "800000"],
      Financeiro: ["800", "1000"],
    },
  }),

  methods: {
    open(data = {}) {
      this.isOpen = true;
      this.member = Object.assign(defaultMember(), data.technician);
      this.platform = data.platform;
      this.platformsId = [data.platform.id];
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    close() {
      if (this.loading) return;
      this.isOpen = false;
      this.loading = false;
      this.error = false;
      this.member = defaultMember();
    },
    toggleAll(perms) {
      if (!perms) perms = this.permissionsArray;
      const memberPermissions = this.member?.permissions || [];
      perms = perms.map((p) => `${p}`);
      const hasAll = perms.every((p) => memberPermissions.includes(p));
      if (hasAll) {
        this.member.permissions = memberPermissions.filter(
          (p) => !perms.includes(p)
        );
      } else {
        this.member.permissions = [
          ...memberPermissions,
          ...perms.filter((p) => !memberPermissions.includes(p)),
        ];
      }
    },

    reducePermissions,

    async remove() {
      try {
        if (!window.confirm("Tem certeza que deseja remover este técnico?"))
          return;
        this.loading = true;
        this.error = false;
        const response = await TECHNICIANS.remove(
          this.platform.id,
          this.member.id
        );
        this.$emit("success", response);
        this.loading = false;
        this.close();
      } catch (e) {
        this.error = e.message;
        this.loading = false;
      }
    },
    async save() {
      try {
        this.loading = true;
        this.error = false;

        for (const platformId of this.platformsId) {
          await TECHNICIANS.update({
            phone: this.member.phone,
            ddi: this.member.ddi,
            permission: this.reducePermissions(this.member.permissions),
            platformId: platformId,
          });
        }

        this.$emit("success");
        this.loading = false;
        this.close();
      } catch (e) {
        this.error = e.message;
        this.loading = false;
      }
    },
  },
  computed: {
    permissionsArray() {
      return Object.values(this.permissionsGroups).flat();
    },
  },
  mounted() {
    this.$root.$on("technician-member-modal", this.open);
  },
};
</script>

<style></style>
